/**
 * Event Model
 * Business logic and helpers for event entities
 */

import { formatBs } from '@/lib/money';
import {
  Event,
  EventSession,
  EventStatus,
  EVENT_STATUS_LABELS,
  PAYMENT_STATUS_LABELS,
  ATTENDANCE_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  PaymentStatus,
  AttendanceStatus,
  PaymentMethod,
} from '@/types/event.types';

// Re-export main types
export type { Event, EventSession };

// =====================================================
// DATE HELPERS
// =====================================================

/**
 * 'YYYY-MM-DD' → Date at local midnight. `new Date('YYYY-MM-DD')` is UTC
 * midnight, i.e. the previous evening in Bolivia (UTC-4) → off-by-one days.
 */
export function parseLocalDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return y && m && d ? new Date(y, m - 1, d) : undefined;
}

// =====================================================
// STATUS HELPERS
// =====================================================

/**
 * Check if event is in draft status
 */
export function isDraft(event: Event): boolean {
  return event.status === 'DRAFT';
}

/**
 * Check if event is open for registration
 */
export function isOpen(event: Event): boolean {
  return event.status === 'OPEN';
}

/**
 * Check if event is closed for registration
 */
export function isClosed(event: Event): boolean {
  return event.status === 'CLOSED';
}

/**
 * Check if event is completed
 */
export function isCompleted(event: Event): boolean {
  return event.status === 'COMPLETED';
}

/**
 * Check if event accepts new registrations
 */
export function acceptsRegistrations(event: Event): boolean {
  return event.status === 'OPEN';
}

/**
 * Check if event is in the past
 */
export function isPast(event: Event): boolean {
  const today = new Date();
  const endDate = new Date(event.end_date);
  return endDate < today;
}

/**
 * Check if event is ongoing (today is between start and end dates)
 */
export function isOngoing(event: Event): boolean {
  const today = new Date();
  const startDate = new Date(event.start_date);
  const endDate = new Date(event.end_date);
  return today >= startDate && today <= endDate;
}

/**
 * Check if event is upcoming (start date is in the future)
 */
export function isUpcoming(event: Event): boolean {
  const today = new Date();
  const startDate = new Date(event.start_date);
  return startDate > today;
}

/**
 * Get allowed status transitions from current status
 */
export function getAllowedStatusTransitions(
  currentStatus: EventStatus,
): EventStatus[] {
  switch (currentStatus) {
    case 'DRAFT':
      return ['OPEN'];
    case 'OPEN':
      return ['CLOSED', 'DRAFT'];
    case 'CLOSED':
      return ['COMPLETED', 'OPEN'];
    case 'COMPLETED':
      return []; // No transitions from completed
    default:
      return [];
  }
}

/**
 * Check if status transition is allowed
 */
export function canTransitionTo(
  current: EventStatus,
  target: EventStatus,
): boolean {
  return getAllowedStatusTransitions(current).includes(target);
}

// =====================================================
// DISPLAY FORMATTERS
// =====================================================

/**
 * Get localized status label
 */
export function getStatusLabel(status: EventStatus): string {
  return EVENT_STATUS_LABELS[status];
}

/**
 * Get status color for UI (Tailwind classes)
 */
export function getStatusColor(status: EventStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'gray';
    case 'OPEN':
      return 'green';
    case 'CLOSED':
      return 'orange';
    case 'COMPLETED':
      return 'blue';
    default:
      return 'gray';
  }
}

/**
 * Get status badge variant
 * Maps event status to valid Badge component variants
 */
export function getStatusBadgeVariant(
  status: EventStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'DRAFT':
      return 'secondary';
    case 'OPEN':
      return 'default';
    case 'CLOSED':
      return 'destructive';
    case 'COMPLETED':
      return 'outline';
    default:
      return 'secondary';
  }
}

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Parse a date-only string (YYYY-MM-DD) without timezone conversion.
 * Using `new Date('YYYY-MM-DD')` parses as UTC midnight, which shifts the
 * displayed date by the local timezone offset (e.g. Apr 6 → Apr 5 in UTC-4).
 */
function parseDateParts(dateStr: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateStr.split('-').map(Number);
  return { year, month, day };
}

/**
 * Format date range (e.g., "15-17 Marzo 2026" or "15 Marzo 2026")
 */
export function formatDateRange(event: Event): string {
  const start = parseDateParts(event.start_date);
  const end = parseDateParts(event.end_date);

  const isSameDay = event.start_date === event.end_date;
  const isSameMonth = start.month === end.month && start.year === end.year;

  if (isSameDay) {
    return `${start.day} ${MONTHS[start.month - 1]} ${start.year}`;
  } else if (isSameMonth) {
    return `${start.day}-${end.day} ${MONTHS[start.month - 1]} ${start.year}`;
  } else {
    return `${start.day} ${MONTHS[start.month - 1]} - ${end.day} ${MONTHS[end.month - 1]} ${end.year}`;
  }
}

/**
 * Format single date (e.g., "15 Marzo 2026")
 */
export function formatDate(date: string): string {
  const { year, month, day } = parseDateParts(date);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/**
 * Format time (e.g., "14:30")
 */
export function formatTime(time: string | null): string {
  if (!time) return '';
  // Time comes as "HH:MM:SS" from database
  return time.substring(0, 5);
}

/**
 * Format session time range (e.g., "14:30 - 17:00")
 */
export function formatSessionTimeRange(session: EventSession): string {
  const start = formatTime(session.start_time);
  const end = formatTime(session.end_time);

  if (start && end) {
    return `${start} - ${end}`;
  } else if (start) {
    return start;
  } else if (end) {
    return `hasta ${end}`;
  }

  return '';
}

/**
 * Format currency (Bolivianos)
 */
export function formatPrice(price: number): string {
  return formatBs(price);
}

/**
 * Get days until event starts
 */
export function getDaysUntilStart(event: Event): number {
  const today = new Date();
  const startDate = new Date(event.start_date);
  const diffTime = startDate.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get event duration in days
 */
export function getEventDuration(event: Event): number {
  const start = new Date(event.start_date);
  const end = new Date(event.end_date);
  const diffTime = end.getTime() - start.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
}

/**
 * Get localized payment status label
 */
export function getPaymentStatusLabel(status: PaymentStatus): string {
  return PAYMENT_STATUS_LABELS[status];
}

/**
 * Get localized attendance status label
 */
export function getAttendanceStatusLabel(status: AttendanceStatus): string {
  return ATTENDANCE_STATUS_LABELS[status];
}

/**
 * Get localized payment method label
 */
export function getPaymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_METHOD_LABELS[method];
}

/**
 * Get payment status color
 */
export function getPaymentStatusColor(status: PaymentStatus): string {
  switch (status) {
    case 'PENDING':
      return 'orange';
    case 'PAID':
      return 'green';
    case 'REVERSED':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get attendance status color
 */
export function getAttendanceStatusColor(status: AttendanceStatus): string {
  switch (status) {
    case 'PENDING':
      return 'gray';
    case 'ATTENDED':
      return 'green';
    case 'ABSENT':
      return 'red';
    default:
      return 'gray';
  }
}

// =====================================================
// VALIDATION HELPERS
// =====================================================

/**
 * Validate event dates
 */
export function validateEventDates(
  startDate: Date,
  endDate: Date,
  allowPastDates: boolean = false,
): string | null {
  if (startDate > endDate) {
    return 'La fecha de inicio debe ser anterior a la fecha de finalización';
  }

  // Only validate against past dates for new events
  if (!allowPastDates) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate < today) {
      return 'La fecha de inicio no puede ser en el pasado';
    }
  }

  return null;
}

/**
 * Validate event code format (EVT-YYYY-NNN)
 */
export function validateEventCode(code: string): string | null {
  // Used as the public registration link (inscripcion.addbolivia.org/CODE):
  // short, uppercase letters/digits, single dashes between groups.
  if (!/^[A-Z0-9]+(-[A-Z0-9]+)*$/.test(code) || code.length < 3 || code.length > 20) {
    return 'El código debe tener de 3 a 20 caracteres: letras, números y guiones (ej: CONV26)';
  }

  return null;
}

/** User input → code format: uppercase, no accents, spaces → "-", only A-Z 0-9 "-", max 20 */
export function normalizeEventCode(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^A-Z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-/, '')
    .slice(0, 20);
}

const CODE_STOPWORDS = new Set([
  'DE', 'DEL', 'LA', 'LAS', 'LOS', 'EL', 'Y', 'EN', 'A', 'AL', 'PARA', 'POR', 'CON', 'UN', 'UNA',
]);
const ROMAN_NUMERAL = /^(?=.{2,})M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;

/**
 * Suggest a short, speakable code from the event name (and year):
 *   "Convención Nacional Ordinaria 2026" → CNO26   (initials of 2+ words)
 *   "Jóvenes" + start 2026               → JOVEN26 (first 5 letters of a single word)
 * Ignores articles/prepositions, numbers and roman numerals ("XXXII Convención…").
 * Returns '' when the name has no usable words yet.
 */
export function suggestEventCode(name: string, startDate?: Date | null): string {
  const tokens = name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean);

  const yearToken = tokens.find((t) => /^(19|20)\d{2}$/.test(t));
  const words = tokens.filter((t) => !CODE_STOPWORDS.has(t) && !/^\d+$/.test(t) && !ROMAN_NUMERAL.test(t));
  if (words.length === 0) return '';

  const base = words.length === 1 ? words[0].slice(0, 5) : words.slice(0, 4).map((w) => w[0]).join('');
  const year = yearToken ?? (startDate ? String(startDate.getFullYear()) : '');
  return normalizeEventCode(year ? `${base}${year.slice(-2)}` : base);
}

/**
 * Validate session times
 */
export function validateSessionTimes(
  startTime: string | null,
  endTime: string | null,
): string | null {
  if (!startTime || !endTime) {
    return null; // Times are optional
  }

  if (startTime >= endTime) {
    return 'La hora de inicio debe ser anterior a la hora de finalización';
  }

  return null;
}

// =====================================================
// BUSINESS LOGIC HELPERS
// =====================================================

/**
 * Calculate event capacity utilization percentage
 */
export function getCapacityUtilization(
  registered: number,
  capacity: number,
): number {
  if (capacity === 0) return 0;
  return Math.round((registered / capacity) * 100);
}

/**
 * Check if event is fully booked
 */
export function isFullyBooked(registered: number, capacity: number): boolean {
  return capacity > 0 && registered >= capacity;
}

/**
 * Get event status summary for dashboard
 */
export function getEventSummary(event: Event): string {
  if (isCompleted(event)) {
    return 'Evento completado';
  } else if (isOngoing(event)) {
    return 'En curso';
  } else if (isUpcoming(event)) {
    const days = getDaysUntilStart(event);
    return days === 0
      ? 'Comienza hoy'
      : `Comienza en ${days} ${days === 1 ? 'día' : 'días'}`;
  } else if (isPast(event)) {
    return 'Finalizado';
  }
  return getStatusLabel(event.status as EventStatus);
}

/**
 * Sort sessions chronologically: date, then start time (sessions without a time go last),
 * then display order as the tie-breaker.
 */
export function sortSessions(sessions: EventSession[]): EventSession[] {
  return [...sessions].sort(
    (a, b) =>
      a.session_date.localeCompare(b.session_date) ||
      (a.start_time ?? '99:99:99').localeCompare(b.start_time ?? '99:99:99') ||
      (a.display_order || 0) - (b.display_order || 0),
  );
}

/**
 * Group sessions by date
 */
export function groupSessionsByDate(
  sessions: EventSession[],
): Map<string, EventSession[]> {
  const grouped = new Map<string, EventSession[]>();

  // Sorted first so the dates come out in calendar order too
  sortSessions(sessions).forEach((session) => {
    const date = session.session_date;
    if (!grouped.has(date)) {
      grouped.set(date, []);
    }
    grouped.get(date)!.push(session);
  });

  // Sort sessions within each date
  grouped.forEach((sessions, date) => {
    grouped.set(date, sortSessions(sessions));
  });

  return grouped;
}
